import React, { useEffect, useState, useMemo, useCallback, useRef, memo, } from "react";
import groupBy from "lodash/groupBy";
import { useOnClickOutside } from "@smartsuite/react-ui/lib/hooks/onClickOutside/useOnClickOutside";
import { useMenuNavigation } from "../../../../core/suggest/useMenuNavigation";
import { canStartSuggester } from "../../../../core/suggest/suggest";
import { EditorCursorPopup } from "../../../../extensions/popup/components/EditorCursorPopup/EditorCursorPopup";
import { SlashSuggesterMenu, } from "../SlashSuggesterMenu/SlashSuggesterMenu";
import * as text from "./SlashSuggester.text";
import { slashCommandGroupOrder } from "./SlashSuggester.text";
export const SlashSuggester = memo(function SlashSuggester({ view, suggesterCallbacksRegistry, commands, }) {
    const [visible, setVisible] = useState(false);
    const [suggesterParams, setSuggesterParams] = useState(null);
    const menuWrapperRef = useRef();
    // filter the visible commands by using the suggester query
    const filteredCommands = useMemo(() => filterCommands(commands, suggesterParams === null || suggesterParams === void 0 ? void 0 : suggesterParams.queryText.partial), [commands, suggesterParams === null || suggesterParams === void 0 ? void 0 : suggesterParams.queryText.partial]);
    // group commands by using `group` field
    const groupedCommands = useMemo(() => groupCommands(filteredCommands), [filteredCommands]);
    // handle menu navigation with arrow keys
    const navigation = useMenuNavigation({ length: filteredCommands.length });
    // close suggester when clicked outside it
    useOnClickOutside(useMemo(() => [menuWrapperRef], []), useCallback(() => {
        if (visible) {
            setVisible(false);
            // after clicking outside, avoid suggester to be opened again until starter character is inserted again
            suggesterParams.addIgnored({
                from: suggesterParams.range.from,
                char: suggesterParams.suggester.char,
                name: suggesterParams.suggester.name,
            });
        }
    }, [visible, suggesterParams]));
    const handleCommand = useCallback((command) => {
        suggesterParams.command(command);
        setVisible(false);
        setSuggesterParams(null);
    }, [suggesterParams]);
    // defines whether the suggester is supposed to show
    const shouldRender = visible && !!suggesterParams && !!filteredCommands.length;
    useEffect(() => {
        suggesterCallbacksRegistry.register({
            onChange: (params) => {
                setSuggesterParams(params);
                if (!suggesterParams &&
                    canStartSuggester(params.view, params.range.from)) {
                    setVisible(true);
                }
            },
            onExit: () => {
                setVisible(false);
                setSuggesterParams(null);
            },
            Escape: ({ event, addIgnored, range, suggester }) => {
                if (!shouldRender) {
                    return false;
                }
                event.stopPropagation(); // avoid closing the modal containing the editor in many situations
                setVisible(false);
                setSuggesterParams(null);
                // after Escape is pressed, avoid suggester to be opened again until starter character is inserted again
                addIgnored({
                    from: range.from,
                    char: suggester.char,
                    name: suggester.name,
                });
                return true;
            },
            Enter: ({ event }) => {
                if (!shouldRender) {
                    return false;
                }
                event.stopPropagation();
                handleCommand(filteredCommands[navigation.activeIndex]);
                return true;
            },
            ArrowDown: () => {
                if (!shouldRender) {
                    return false;
                }
                navigation.down();
                return true;
            },
            ArrowUp: () => {
                if (!shouldRender) {
                    return false;
                }
                navigation.up();
                return true;
            },
        });
    }, [
        suggesterCallbacksRegistry,
        filteredCommands,
        navigation,
        shouldRender,
        handleCommand,
        suggesterParams,
    ]);
    if (!shouldRender) {
        return null;
    }
    return (React.createElement(EditorCursorPopup, { view: view, from: suggesterParams.range.from, to: suggesterParams.range.to, placement: "bottom-start", flipEnabled: true },
        React.createElement("div", { ref: menuWrapperRef, className: "slash-suggester-wrapper" },
            React.createElement(SlashSuggesterMenu, { commands: groupedCommands, activeIndex: navigation.activeIndex, onCommand: handleCommand }))));
});
/**
 * Filters the list of commands based on given query.
 * Also sorts and assigns a index value to each item, making it ready for grouping.
 */
function filterCommands(commands, query) {
    let filteredCommands = commands;
    if (query) {
        filteredCommands = filteredCommands.filter((item) => {
            var _a;
            const itemLabel = item.label.toLocaleLowerCase();
            const itemDescription = item.description.toLowerCase();
            const itemShortcut = (_a = item.shortcut) !== null && _a !== void 0 ? _a : "";
            return (itemLabel.includes(query) ||
                itemDescription.includes(query) ||
                itemShortcut.includes(query));
        });
    }
    return filteredCommands
        .sort((a, b) => {
        return (slashCommandGroupOrder.indexOf(text.slashCommandGroupLabels[a.group]) -
            slashCommandGroupOrder.indexOf(text.slashCommandGroupLabels[b.group]));
    })
        .map((item, index) => ({ ...item, index }));
}
/**
 * Groups the list of commands based on the `command.group` field.
 */
function groupCommands(filteredCommands) {
    const groups = groupBy(filteredCommands, (item) => item.group);
    return Object.keys(groups)
        .map((group) => ({
        name: text.slashCommandGroupLabels[group],
        items: groups[group],
    }))
        .sort((a, b) => slashCommandGroupOrder.indexOf(a.name) -
        slashCommandGroupOrder.indexOf(b.name));
}
